/**
 * ==============================================================================
 * MARK DUTY MÉXICO - TACTICAL SCROLLCRAFT & INTERACTIVE HUD ENGINE
 * Controla:
 * 1. Ficha técnica y descripción desplegable por producto (HUD).
 * 2. Marquesina de anuncios reactiva con aceleración por velocidad de scroll.
 * 3. Logotipo 3D interactivo con inclinación por puntero (Pointer Tilt).
 * 4. Revelación cinética de tarjetas y contadores al hacer scroll.
 * ==============================================================================
 */

(function () {
  'use strict';

  // 1. CONTROL DE DESCRIPCIÓN Y ESPECIFICACIONES TÁCTICAS
  window.toggleTacticalSpecs = function (btn, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const isExpanded = btn.getAttribute('aria-expanded') === 'true';
    const targetId = btn.getAttribute('aria-controls');
    const drawer = document.getElementById(targetId);

    if (!drawer) return;

    if (isExpanded) {
      btn.setAttribute('aria-expanded', 'false');
      drawer.hidden = true;
    } else {
      // Cerrar otros drawers abiertos para mantener la cuadrícula limpia y evitar desfases
      document.querySelectorAll('.tactical-desc-toggle-btn[aria-expanded="true"]').forEach(otherBtn => {
        if (otherBtn !== btn) {
          otherBtn.setAttribute('aria-expanded', 'false');
          const otherId = otherBtn.getAttribute('aria-controls');
          const otherDrawer = document.getElementById(otherId);
          if (otherDrawer) otherDrawer.hidden = true;
        }
      });

      btn.setAttribute('aria-expanded', 'true');
      drawer.hidden = false;

      // Reiniciar animación del láser esquemático para efecto visual inmediato
      const laser = drawer.querySelector('.blueprint-scan-line');
      if (laser) {
        laser.style.animation = 'none';
        void laser.offsetWidth; // Forzar reflow
        laser.style.animation = 'tacticalLaserSweep 3.2s cubic-bezier(0.4, 0, 0.2, 1) infinite';
      }

      // Asegurar que el contenido desplegado sea visible en pantalla si queda muy abajo
      setTimeout(() => {
        const rect = drawer.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          window.scrollBy({ top: Math.min(rect.bottom - window.innerHeight + 40, 250), behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // 2. MARQUESINA DE ANUNCIOS ACELERADA POR VELOCIDAD DE SCROLL (SCROLLCRAFT RUNTIME)
  function initScrollAcceleratedMarquee() {
    const track = document.querySelector('.tactical-marquee-track');
    const container = document.querySelector('.tactical-marquee-container');
    if (!track) return;

    let baseSpeed = 0.75; // Píxeles por frame a velocidad normal
    let currentX = 0;
    let extraVelocity = 0;
    let lastScrollY = window.scrollY;
    let isHovered = false;

    // Detectar velocidad de scroll del usuario
    window.addEventListener('scroll', function () {
      const currentScrollY = window.scrollY;
      const delta = Math.abs(currentScrollY - lastScrollY);
      // Impulso de aceleración proporcional a la velocidad del scroll
      extraVelocity += delta * 0.12;
      // Límite máximo para mantener legibilidad
      if (extraVelocity > 14) extraVelocity = 14;
      lastScrollY = currentScrollY;
    }, { passive: true });

    // Pausar o ralentizar al pasar el cursor
    if (container) {
      container.addEventListener('mouseenter', () => { isHovered = true; });
      container.addEventListener('mouseleave', () => { isHovered = false; });
      container.addEventListener('touchstart', () => { isHovered = true; }, { passive: true });
      container.addEventListener('touchend', () => { isHovered = false; }, { passive: true });
    }

    // Cancelar la animación CSS pura para tomar el control por rAF suave
    track.style.animation = 'none';

    function animate() {
      // Desaceleración elástica por fricción (Friction Damping)
      extraVelocity *= 0.92;
      if (extraVelocity < 0.01) extraVelocity = 0;

      let step = isHovered ? 0.2 : (baseSpeed + extraVelocity);
      currentX -= step;

      const halfWidth = track.scrollWidth / 2;
      if (Math.abs(currentX) >= halfWidth) {
        currentX = 0;
      }

      track.style.transform = `translate3d(${currentX}px, 0, 0)`;
      requestAnimationFrame(animate);
    }

    requestAnimationFrame(animate);
  }

  // 3. LOGOTIPO TÁCTICO 3D CON INCLINACIÓN DE PUNTERO (POINTER TILT)
  function initTacticalLogoTilt() {
    const header = document.querySelector('.tactical-brand-header');
    const logo = document.querySelector('.tactical-main-logo');
    if (!header || !logo) return;

    let targetRotX = 0;
    let targetRotY = 0;
    let currentRotX = 0;
    let currentRotY = 0;
    let isTracking = false;

    header.addEventListener('mousemove', function (e) {
      const rect = header.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Rotación máxima de 8 grados para mantener elegancia militar
      targetRotY = ((x - centerX) / centerX) * 8;
      targetRotX = -((y - centerY) / centerY) * 8;
      isTracking = true;
    });

    header.addEventListener('mouseleave', function () {
      targetRotX = 0;
      targetRotY = 0;
      isTracking = false;
    });

    function renderTilt() {
      // Lerp suave (Linear Interpolation)
      currentRotX += (targetRotX - currentRotX) * 0.1;
      currentRotY += (targetRotY - currentRotY) * 0.1;

      if (isTracking || Math.abs(currentRotX) > 0.05 || Math.abs(currentRotY) > 0.05) {
        logo.style.transform = `perspective(1000px) rotateX(${currentRotX.toFixed(2)}deg) rotateY(${currentRotY.toFixed(2)}deg) scale(1.02)`;
      } else {
        logo.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
      }

      requestAnimationFrame(renderTilt);
    }

    requestAnimationFrame(renderTilt);
  }

  // 4. REVELACIÓN CINÉTICA AL HACER SCROLL (SCROLLCRAFT INTERSECTION OBSERVER)
  function initKineticScrollReveals() {
    if (!('IntersectionObserver' in window)) return;

    const cards = document.querySelectorAll('.product-card, .tactical-benefit-card, .wholesale-form-card');
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    cards.forEach(function (card, index) {
      card.style.opacity = '0';
      card.style.transform = 'translateY(24px)';
      card.style.transition = `opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.08}s, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.08}s`;
      observer.observe(card);
    });
  }

  // INICIALIZACIÓN AL CARGAR DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initScrollAcceleratedMarquee();
      initTacticalLogoTilt();
      initKineticScrollReveals();
    });
  } else {
    initScrollAcceleratedMarquee();
    initTacticalLogoTilt();
    initKineticScrollReveals();
  }
})();
